import { Injectable, Injector } from '@angular/core';
import {
    AuthConfig,
    OAuthErrorEvent,
    OAuthEvent,
    OAuthService,
} from 'angular-oauth2-oidc';
import { AppConfig } from '@app/app.config';
import { BehaviorSubject, from, Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private _translate?: TranslateService;
    private authEventsSubscription?: Subscription;
    private _configured$ = new BehaviorSubject<boolean>(false);
    public configured$ = this._configured$.asObservable();
    private _isConfigured = false;

    constructor(
        private oAuthService: OAuthService,
        private appConfig: AppConfig,
        private injector: Injector,
        private router: Router
    ) {}

    private get translateService(): TranslateService {
        if (!this._translate) {
            this._translate = this.injector.get(TranslateService);
        }
        return this._translate;
    }

    public async configureOAuthService() {
        this.oAuthService.setStorage(localStorage);
        await this.appConfig.load();
        const authConfig: AuthConfig = {
            issuer: this.appConfig.oAuthIssuer,
            redirectUri: window.location.origin + '/login-callback',
            clientId: this.appConfig.oAuthClientId,
            scope: 'openid profile email offline_access',
            responseType: 'code',
            showDebugInformation: false,
            requireHttps: this.appConfig.oAuthForceHttps,
            useSilentRefresh: false,
            disableAtHashCheck: true,
            strictDiscoveryDocumentValidation: false,
        };

        this.oAuthService.configure(authConfig);
        await this.oAuthService.loadDiscoveryDocument();
        this._isConfigured = true;
        this.oAuthService.setupAutomaticSilentRefresh();
        this.subscribeToAuthEvents();
        this._configured$.next(true);
    }

    public initLogin(redirectUrl: string | null = null): void {
        redirectUrl = redirectUrl ? redirectUrl : '/';

        this.oAuthService.loadDiscoveryDocument().then(() =>
            this.oAuthService.initCodeFlow('entrypoint=' + redirectUrl, {
                ui_locales: this.getKeycloakUiLocales(),
            })
        );
    }

    public getKeycloakUiLocales() {
        const allowedUiLocales = ['de', 'en'];
        const currentLang = this.translateService.currentLang;
        const uiLocales = allowedUiLocales.includes(currentLang)
            ? currentLang
            : 'en';
        return uiLocales;
    }

    public login(): Observable<boolean> {
        return from(this.oAuthService.loadDiscoveryDocumentAndTryLogin());
    }

    /**
     * Gets the OAuth state parameter
     */
    public getOAuthState(): string | null {
        return this.oAuthService.state || null;
    }

    /**
     * Parses the OAuth state to extract redirect route information
     */
    public parseOAuthState(state: string): { r?: string } | null {
        try {
            return JSON.parse(atob(state));
        } catch (error) {
            return null;
        }
    }

    logout(noRedirect: boolean = false): void {
        this.oAuthService.logOut(noRedirect);
    }

    /**
     * Check if the user is fully authenticated (has a valid access token, not expired)
     */
    isAuthenticated(): boolean {
        return this.oAuthService.hasValidAccessToken();
    }

    /**
     * Check if the user is logged in. (has a valid token or can refresh the token)
     * Strangely I have to sometimes manually trigger the token refresh. This happens if the user closed the tab/browser
     * for a longer time and then comes back to the app. The access token is then expired but oauth-oidc does not seem
     * to automatically refresh the token for some reason.
     */
    async isLoggedIn() {
        try {
            // Guard: Don't attempt refresh if OAuth not configured yet
            if (!this._isConfigured) {
                return this.oAuthService.hasValidAccessToken();
            }

            // check if access token and refresh token are present in storage
            const accessToken = this.oAuthService.getAccessToken();
            const refreshToken = this.oAuthService.getRefreshToken();
            if (!accessToken || !refreshToken) {
                return false;
            }

            await this.oAuthService.refreshToken();
        } catch (e) {
            console.log('error refreshing token: ', e);
            return this.oAuthService.hasValidAccessToken();
        }
        return this.oAuthService.hasValidAccessToken();
    }

    private subscribeToAuthEvents(): void {
        // Unsubscribe from any existing subscription
        this.authEventsSubscription?.unsubscribe();

        // Create a new subscription
        this.authEventsSubscription = this.oAuthService.events.subscribe(
            (event: OAuthEvent) => {
                if (
                    event.type === 'session_terminated' ||
                    this.isTokenRefreshError(event)
                ) {
                    const sessionService = this.injector.get(
                        SessionService,
                        null
                    );
                    sessionService?.deleteSession();
                    this.router.navigate(['/login-callback']);
                }
            }
        );
    }

    private isTokenRefreshError(event: OAuthEvent): boolean {
        if (event.type === 'token_refresh_error') {
            return ((event as OAuthErrorEvent).reason as any).status === 400;
        }
        return false;
    }

    /**
     * Returns the stored redirect route from sessionStorage (saved before BFF login redirect).
     */
    public rememberPostLoginRedirect(redirectUrl: string): void {
        sessionStorage.setItem('bff_redirect_route', redirectUrl);
    }

    public getPostLoginRedirect(): string {
        return sessionStorage.getItem('bff_redirect_route') || '/';
    }

    /**
     * Clears the stored redirect route.
     */
    public clearPostLoginRedirect(): void {
        sessionStorage.removeItem('bff_redirect_route');
    }
}
